import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// 30 minutes in milliseconds
const IDLE_LIMIT = 30 * 60 * 1000; 

export const useIdleTimeout = () => {
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // The actual logout action
  const handleLogout = useCallback(async () => {
    if (isAuthenticated) {
      console.log('User inactive. Logging out...');
      await logout();
      navigate('/login'); 
    }
  }, [logout, navigate, isAuthenticated]);

  // Function to reset the timer whenever user does something
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Only set the timer if the user is actually logged in
    if (isAuthenticated) {
      timerRef.current = setTimeout(handleLogout, IDLE_LIMIT);
    }
  }, [isAuthenticated, handleLogout]);

  useEffect(() => {
    // If user is not authenticated, do nothing
    if (!isAuthenticated) return;

    // Events that count as "activity"
    const events = [
      'mousedown', 
      'mousemove', 
      'keydown', 
      'scroll', 
      'touchstart', 
      'click'
    ];

    // Initialize timer
    resetTimer();

    // Add event listeners
    const handleActivity = () => resetTimer();
    
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Cleanup
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, resetTimer]);
};